// @ts-strict-ignore
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import theme from "styles/theme";
import Player3D from "components/product-media/player-3d";
import { IThreediumAPI, IThreediumError, IThreediumTemplate } from "constants/product-customizations/threedium";
import { Threedium } from "./threedium";
import { IThreediumAttribute, IThreediumCustomization } from "constants/product-customizations/customization";
import classnames from "classnames";

// timeout is in seconds
export const sleep = (timeout: number): Promise<void> =>
    new Promise((resolve, _) => setTimeout(resolve, Math.round(timeout * 1000)));

const RETRY_COUNT = 3;

const useViewStyles = makeStyles({
    container: { height: "100%", width: "100%" },
    apiError: {
        position: "absolute",
        zIndex: 10,
        left: "16px",
        right: "16px",
        color: theme.palette.errorColor,
        textAlign: "center",
        wordBreak: "break-word",
        padding: "8px",
        backgroundColor: theme.palette.errorAlertBackground,
        border: `1px solid ${theme.palette.errorAlertBorderColor}`,
        borderRadius: "5px",
        opacity: 0,
        transition: "opacity 600ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
        "&.visible": {
            opacity: 1,
        },
    },
    image3dHint: {
        position: "absolute",
        left: 0,
        right: 0,
        width: "max-content",
        marginLeft: "auto",
        marginRight: "auto",
    },
    image3dWrapper: {
        color: theme.palette.white,
    },
    image3dProgress: {
        width: "60%",
    },
    image3dProgressText: {
        color: theme.palette.darkGrey,
    },
    iframe: {
        width: "100%",
        height: "100%",
    },
});

export interface ICustomizationThreediumPlayerProps {
    template: IThreediumTemplate;
    attribute: IThreediumAttribute;
    customizations?: IThreediumCustomization[];
    onError: (error: IThreediumError, erroredTemplate: IThreediumTemplate) => void;
    onLoadError?: (error: ErrorEvent, erroredTemplate: IThreediumTemplate) => void;
    onModelLoad: (helper: Threedium) => void;
}

const Player: FC<ICustomizationThreediumPlayerProps> = ({
    template,
    attribute,
    customizations = [],
    onError,
    onLoadError,
    onModelLoad,
}) => {
    const threedium = useRef<Threedium>();
    const threediumOptions = useRef<IThreediumTemplate>();
    const modelLoaded = useRef(false);
    const initializing = useRef(true);
    const [shouldApply, setShouldApply] = useState(false);

    const retryCounter = useRef(0);

    // this is used for reloading purposes, if threedium was unreachable for some reason
    const [currentTemplate, setCurrentTemplate] = useState<IThreediumTemplate>(null);
    const [apiError, setApiError] = useState<string>(null);
    const [showApiError, setShowApiError] = useState(false);

    const classes = useViewStyles({});

    useEffect(() => {
        modelLoaded.current = false;
        initializing.current = true;
        setCurrentTemplate(template);
        setShowApiError(false);
        setApiError(null);
        setShouldApply(false);
    }, [template]);

    const applyCustomization = useCallback(
        (animated = true) => threedium.current.applyCustomizations(attribute, customizations, animated),
        [attribute, customizations, threedium],
    );

    useEffect(() => {
        if (!shouldApply || !initializing.current) {
            return;
        }

        applyCustomization(false)
            // TODO: hack to switch over to next tick to allow threedium to wrap its stuff
            .then(() => sleep(0))
            .then(() => {
                modelLoaded.current = true;
                initializing.current = false;
                setShouldApply(false);
                onModelLoad?.(threedium.current);
            });
    }, [shouldApply, applyCustomization, onModelLoad]);

    useEffect(() => {
        if (modelLoaded.current && !initializing.current) {
            applyCustomization();
        }
    }, [applyCustomization, modelLoaded]);

    // reload on error RETRY_COUNT times
    const initRetry = useCallback(
        (error) => {
            const retryCount = retryCounter.current;
            if (retryCount >= RETRY_COUNT) {
                retryCounter.current = 0;
                onError(error, currentTemplate);

                return;
            }

            setTimeout(() => {
                retryCounter.current = retryCount + 1;
                setCurrentTemplate((currentTemplate) => ({
                    ...currentTemplate,
                }));
            });
        },
        [onError, retryCounter, currentTemplate],
    );

    const handleError = useCallback(
        (error: IThreediumError) => {
            initRetry(error);
        },
        [initRetry],
    );

    const handleLoadError = useCallback(
        (error: ErrorEvent) => {
            onLoadError?.(error, currentTemplate);
        },
        [onLoadError, currentTemplate],
    );

    const handleLoad = useCallback(() => {
        threedium.current
            ?.loadModel()
            // not calling applyCustomization() directly cause sometimes its old version
            // is getting stuck as a promise callback here, which is undesired.
            // Instead we set the flag and react on it using useEffect hook above.
            .then(() => setShouldApply(true));
    }, [threedium]);

    const processApiError = useCallback((error, details = "") => {
        const message = error?.message || error?.error || error?.errorMessage || error;
        setApiError(`${message}\n ${details}`.trim());
        setShowApiError(true);
        setTimeout(() => setShowApiError(false), 2000);
        setTimeout(() => setApiError(null), 2500);
    }, []);

    const initHelpers = useCallback(
        (api: IThreediumAPI, options: IThreediumTemplate) => {
            const helper = new Threedium(api, currentTemplate);
            threedium.current = helper;
            threediumOptions.current = options;
            helper.attachErrorListener(processApiError);
        },
        [currentTemplate, threedium, processApiError],
    );

    return (
        <>
            {apiError && <div className={classnames(classes.apiError, { visible: showApiError })}>{apiError}</div>}
            <Player3D
                id={currentTemplate?.containerID}
                version={currentTemplate?.version}
                key={currentTemplate?.distID}
                template={currentTemplate}
                onModelLoad={handleLoad}
                onModelError={handleError}
                onLoadError={handleLoadError}
                apiRef={initHelpers}
                containerStyle={{ width: "100%", height: "100%" }}
                classes={{
                    hint: classes.image3dHint,
                    wrapper: classes.image3dWrapper,
                    iframe: classes.iframe,
                }}
                progressComponent={(progress) => null}
                errorComponent={(error) => null}
            />
        </>
    );
};

export const CustomizationThreediumPlayer: FC<ICustomizationThreediumPlayerProps> = React.memo(Player);
